import React, { useCallback, useEffect, useState } from "react";

import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  TextField,
  Typography
} from "@mui/material";

export interface BankPercentageItem {
  bank_detail_id: number;
  label: string;
}

export interface BankPercentageResult {
  bank_percentages: Array<{ bank_detail_id: number; percentage: number }>;
}

const TOTAL_PERCENT = 100;

interface Props {
  open: boolean;
  banks: BankPercentageItem[];
  savedPercentages?: Record<number, string>;
  onConfirm: (result: BankPercentageResult) => void;
  onCancel: () => void;
}

/**
 * Dialog to collect percentage split across multiple bank accounts.
 * Sum of percentages must equal 100% to enable the Generate button.
 */
function distributeRemainder(total: number, count: number): number[] {
  if (count <= 0) return [];
  // Work in cents to avoid floating-point drift, then convert back.
  const totalCents = Math.round(total * 100);
  const baseCents = Math.floor(totalCents / count);
  const spillCents = totalCents - baseCents * count;
  return Array.from({ length: count }, (_, i) =>
    (i < spillCents ? baseCents + 1 : baseCents) / 100
  );
}

export const BankPercentageDialog: React.FC<Props> = ({ open, banks, savedPercentages, onConfirm, onCancel }) => {
  const [percentages, setPercentages] = useState<Record<number, string>>({});
  const [autoSync, setAutoSync] = useState(true);

  useEffect(() => {
    if (open && banks.length) {
      if (savedPercentages && Object.keys(savedPercentages).length > 0) {
        setPercentages(savedPercentages);
      } else {
        const n = banks.length;
        const values = distributeRemainder(TOTAL_PERCENT, n);
        const initial: Record<number, string> = {};
        banks.forEach((b, index) => {
          initial[b.bank_detail_id] = String(values[index]);
        });
        setPercentages(initial);
      }
    }
  }, [open, banks, savedPercentages]);

  const setPercentage = useCallback(
    (bankDetailId: number, value: string, autoSyncEnabled: boolean) => {
      if (!autoSyncEnabled) {
        setPercentages((prev) => ({ ...prev, [bankDetailId]: value }));
        return;
      }
      const num = Math.round(Number(value) * 100) / 100;
      const clamped = Number.isFinite(num) ? Math.min(100, Math.max(0, num)) : 0;
      const others = banks.filter((b) => b.bank_detail_id !== bankDetailId);
      if (others.length === 0) {
        setPercentages((prev) => ({ ...prev, [bankDetailId]: String(clamped) }));
        return;
      }
      const remaining = TOTAL_PERCENT - clamped;
      const distributed = distributeRemainder(Math.max(0, remaining), others.length);
      setPercentages((prev) => {
        const next = { ...prev, [bankDetailId]: String(clamped) };
        others.forEach((b, i) => {
          next[b.bank_detail_id] = String(distributed[i]);
        });
        return next;
      });
    },
    [banks]
  );

  const numericValues = banks.map((b) => {
    const v = percentages[b.bank_detail_id];
    const n = v === "" || v === "-" ? NaN : Number(v);
    return { bank_detail_id: b.bank_detail_id, value: n };
  });
  const sum = numericValues.reduce((acc, { value }) => acc + (Number.isFinite(value) ? value : 0), 0);
  const allValid = numericValues.every(({ value }) => Number.isFinite(value) && value >= 0);
  const totalValid = allValid && Math.abs(sum - TOTAL_PERCENT) < 1e-6;

  const handleGenerate = useCallback(() => {
    if (!totalValid) return;
    onConfirm({
      bank_percentages: numericValues.map(({ bank_detail_id, value }) => ({
        bank_detail_id,
        percentage: Math.round(value * 10000) / 10000
      }))
    });
  }, [totalValid, numericValues, onConfirm]);

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "8px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)"
        }
      }}
    >
      <DialogTitle
        component="div"
        sx={{
          fontSize: "1.25rem",
          fontWeight: 600,
          pb: 0
        }}
      >
        Report
      </DialogTitle>
      <DialogContent sx={{ pt: 1.5, pb: 0 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Please enter the percentage ratio for each account. The total must equal 100%.
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={autoSync}
              onChange={(_, checked) => setAutoSync(checked)}
              size="small"
            />
          }
          label="Balance automatically to 100%"
          sx={{ mb: 2 }}
        />
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {banks.map((bank) => (
            <Box
              key={bank.bank_detail_id}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2
              }}
            >
              <Typography variant="body2" sx={{ minWidth: 120 }} component="span">
                {bank.label}
              </Typography>
              <TextField
                size="small"
                type="number"
                inputProps={{ min: 0, max: 100, step: 0.01 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography variant="body2" color="text.secondary">
                        %
                      </Typography>
                    </InputAdornment>
                  ),
                  sx: {
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderRadius: 1.5
                    }
                  }
                }}
                value={percentages[bank.bank_detail_id] ?? ""}
                onChange={(e) => setPercentage(bank.bank_detail_id, e.target.value, autoSync)}
                placeholder="0–100"
                sx={{
                  width: 120,
                  "& .MuiInputBase-root": {
                    backgroundColor: "action.hover"
                  }
                }}
              />
            </Box>
          ))}
        </Box>
        <Box
          sx={{
            mt: 2.5,
            py: 1.5,
            px: 2,
            borderRadius: 1.5,
            backgroundColor: "action.selected"
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600 }}
            color={!totalValid ? "error" : "text.primary"}
          >
            Total: {Number.isFinite(sum) ? (Math.round(sum * 100) / 100) : "—"}%
            {!totalValid && " (must equal 100%)"}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          gap: 1.5
        }}
      >
        <Button onClick={onCancel} variant="outlined" sx={{ minWidth: 100 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleGenerate}
          disabled={!totalValid}
          sx={{ minWidth: 140 }}
        >
          Generate Report
        </Button>
      </DialogActions>
    </Dialog>
  );
};
